'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/** Pointer must travel this far before a press becomes a drag (keeps clicks intact). */
const DRAG_THRESHOLD_PX = 5;
/** Re-enable snapping after this long even if the browser never fires `scrollend`. */
const SETTLE_FALLBACK_MS = 700;

interface DragSession {
  pointerId: number;
  startX: number;
  startScrollLeft: number;
  /** True once the pointer travelled past the threshold and the track follows it. */
  engaged: boolean;
}

export interface DragToScrollHandlers {
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: React.PointerEvent<HTMLElement>) => void;
  onClickCapture: (event: React.MouseEvent<HTMLElement>) => void;
  onDragStart: (event: React.DragEvent<HTMLElement>) => void;
}

export interface UseDragToScrollReturn {
  /** True while the pointer is down and moving the track (grabbing cursor). */
  isDragging: boolean;
  /**
   * True from drag start until the post-release snap animation settles.
   * CSS scroll snapping must be off while set — a mandatory snap would fight
   * every scrollLeft write and the release animation.
   */
  isSnapSuspended: boolean;
  /** Spread onto the scrollable element. */
  dragHandlers: DragToScrollHandlers;
}

/**
 * Mouse drag-to-scroll for a native horizontal scroll-snap track.
 *
 * Touch and pen already pan a scroll container natively, so only mouse
 * pointers are handled. A short movement threshold keeps plain clicks (card
 * links, CTAs) working; once a real drag happened, the click that the browser
 * fires on release is captured and cancelled so the card underneath does not
 * navigate. On release the track animates to the nearest child's start edge,
 * mirroring where `scroll-snap-align: start` would settle.
 */
export function useDragToScroll(
  ref: React.RefObject<HTMLElement | null>,
  enabled = true
): UseDragToScrollReturn {
  const [isDragging, setIsDragging] = useState(false);
  const [isSnapSuspended, setIsSnapSuspendedState] = useState(false);
  // Ref mirror so release paths outside React's event system (window-level
  // pointerup, the buttons===0 recovery) can read the current value.
  const snapSuspendedRef = useRef(false);
  const sessionRef = useRef<DragSession | null>(null);
  const suppressClickRef = useRef(false);
  const cancelSettleRef = useRef<(() => void) | null>(null);
  const detachWindowReleaseRef = useRef<(() => void) | null>(null);

  const setSnapSuspended = useCallback((value: boolean) => {
    snapSuspendedRef.current = value;
    setIsSnapSuspendedState(value);
  }, []);

  // Settle/listener callbacks close over DOM listeners and timers — drop them
  // on unmount.
  useEffect(
    () => () => {
      cancelSettleRef.current?.();
      detachWindowReleaseRef.current?.();
    },
    []
  );

  /** Scroll to the nearest child's start edge; false when already there. */
  const snapToNearest = (el: HTMLElement): boolean => {
    // Only cells with a layout box: breakpoint-hidden cards (display: none)
    // report offsetLeft 0 and would corrupt the target math.
    const visible = Array.from(el.children).filter(
      (child): child is HTMLElement => (child as HTMLElement).offsetWidth > 0
    );
    const first = visible[0];
    if (!first) return false;
    let nearest = 0;
    let bestDistance = Infinity;
    for (const child of visible) {
      const position = child.offsetLeft - first.offsetLeft;
      const distance = Math.abs(position - el.scrollLeft);
      if (distance < bestDistance) {
        bestDistance = distance;
        nearest = position;
      }
    }
    if (bestDistance < 1) return false;
    el.scrollTo({ left: nearest, behavior: 'smooth' });
    return true;
  };

  const settle = useCallback(
    (el: HTMLElement) => {
      cancelSettleRef.current?.();
      if (!snapToNearest(el)) {
        setSnapSuspended(false);
        return;
      }
      const finish = () => {
        cancelSettleRef.current?.();
        setSnapSuspended(false);
      };
      const timer = setTimeout(finish, SETTLE_FALLBACK_MS);
      el.addEventListener('scrollend', finish, { once: true });
      cancelSettleRef.current = () => {
        clearTimeout(timer);
        el.removeEventListener('scrollend', finish);
        cancelSettleRef.current = null;
      };
    },
    [setSnapSuspended]
  );

  /**
   * Close the session from any release path (track pointerup, window-level
   * pointerup for releases outside the track, buttons===0 recovery). Also
   * finishes an interrupted settle so snapping is never left suspended.
   */
  const finishSession = useCallback(
    (pointerId: number) => {
      const session = sessionRef.current;
      if (!session || pointerId !== session.pointerId) return;
      sessionRef.current = null;
      detachWindowReleaseRef.current?.();
      const el = ref.current;
      if (!el) return;
      if (session.engaged) {
        suppressClickRef.current = true;
        setTimeout(() => {
          suppressClickRef.current = false;
        }, 0);
        setIsDragging(false);
        settle(el);
      } else if (snapSuspendedRef.current) {
        // Press that interrupted a settle but never became a drag: finish the
        // settle so snapping cannot stay suspended.
        settle(el);
      }
    },
    [ref, settle]
  );

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const el = ref.current;
      if (!enabled || !el) return;
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (el.scrollWidth <= el.clientWidth + 1) return;
      // Grabbing the track mid-settle keeps snapping off; every release path
      // funnels into finishSession, which re-settles.
      cancelSettleRef.current?.();
      detachWindowReleaseRef.current?.();
      sessionRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startScrollLeft: el.scrollLeft,
        engaged: false,
      };
      // Before the threshold engages pointer capture, a release outside the
      // track never reaches the element's own pointerup handler.
      const onWindowRelease = (nativeEvent: PointerEvent) => finishSession(nativeEvent.pointerId);
      window.addEventListener('pointerup', onWindowRelease);
      window.addEventListener('pointercancel', onWindowRelease);
      detachWindowReleaseRef.current = () => {
        window.removeEventListener('pointerup', onWindowRelease);
        window.removeEventListener('pointercancel', onWindowRelease);
        detachWindowReleaseRef.current = null;
      };
    },
    [enabled, finishSession, ref]
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const session = sessionRef.current;
      const el = ref.current;
      if (!session || !el || event.pointerId !== session.pointerId) return;
      // Belt and braces for a missed release (window listener detached, tab
      // switch mid-press, …): close the session properly.
      if (event.buttons === 0) {
        finishSession(event.pointerId);
        return;
      }
      const deltaX = event.clientX - session.startX;
      if (!session.engaged) {
        if (Math.abs(deltaX) < DRAG_THRESHOLD_PX) return;
        session.engaged = true;
        setIsDragging(true);
        setSnapSuspended(true);
        el.setPointerCapture?.(event.pointerId);
      }
      el.scrollLeft = session.startScrollLeft - deltaX;
    },
    [finishSession, ref, setSnapSuspended]
  );

  const onPointerRelease = useCallback(
    (event: React.PointerEvent<HTMLElement>) => finishSession(event.pointerId),
    [finishSession]
  );

  const onClickCapture = useCallback((event: React.MouseEvent<HTMLElement>) => {
    if (!suppressClickRef.current) return;
    suppressClickRef.current = false;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  // Native image/link dragging is the same gesture this hook reinterprets, so
  // block it only while a press on a scrollable track is being tracked — on
  // non-slider breakpoints and non-overflowing tracks it stays available.
  const onDragStart = useCallback((event: React.DragEvent<HTMLElement>) => {
    if (sessionRef.current) event.preventDefault();
  }, []);

  return {
    isDragging,
    isSnapSuspended,
    dragHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: onPointerRelease,
      onPointerCancel: onPointerRelease,
      onClickCapture,
      onDragStart,
    },
  };
}
